/* eslint-disable no-empty */
import React, { FC, useEffect, useState } from 'react';
import {
    PrimaryButton,
    MessageBar,
    MessageBarType,
    IDropdownOption,
    Icon,
    Spinner,
    SpinnerSize,
    TooltipHost,
    DirectionalHint,
} from '@fluentui/react';
import { Dialog, Checkbox, Toggle, Dropdown } from '@eta/components';
import { useTranslation } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';
import { isNull } from 'util';
import { updateTaxpayerNotifications } from '../Redux/Actions';
import { ReduxState } from '../../common/reducers';
import {
    RecipientPreferenceRequestDto,
    RecipientUserPreferenceDto,
    NotificationTypes,
    RecipientNotificationPreferenceRequestDto,
    NotificationChannels,
} from '../../Services/ClientProxy/NotificationManagementClient';

import './NotificationPreferences.scss';
import { Spacer } from '../../common/component/Spacer';
import { ServiceCreator } from '../../Services/ClientProxy/ServiceFactory';

const mapDispatchToProps = {
    updateNotifications: updateTaxpayerNotifications,
};

const mapStateToProps = (state: ReduxState) => ({
    lastError: state.taxpayerProfile.lastError,
});

export interface NotificationPreferencesProps {
    hidden: boolean;
    onClose: () => void;
    onSave: () => void;
    notifications?: RecipientUserPreferenceDto;
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const NotificationPreferences: FC<
    PropsFromRedux & NotificationPreferencesProps
> = (props: PropsFromRedux & NotificationPreferencesProps) => {
    const {
        notifications,
        updateNotifications,
        onSave,
        lastError,
        hidden,
        onClose,
    } = props;
    const { t } = useTranslation(['resources']);
    const [notificationChannel, setNotificationChannel] = useState<number>();
    const [batchInterval, setBatchInterval] = useState<number>();
    const [allDocumentNotification, setAllDocumentNotification] =
        useState(false);
    const [, setDocumentNotification] = useState(false);
    const [documentReceived, setDocumentReceived] = useState(false);
    const [documentCancelled, setDocumentCancelled] = useState(false);
    const [documentRejected, setDocumentRejected] = useState(false);
    const [documentValidated, setDocumentValidated] = useState(false);
    const [packageReady, setPackageReady] = useState(false);
    const [CodesRequests, setCodesRequests] = useState(false);

    const [receiptNotifications, setReceiptNotifications] = useState(false);
    const [receiptProcessingComplete, setReceiptProcessingComplete] =
        useState(false);
    const [receiptReceivedByTaxpayer, setReceiptReceivedByTaxpayer] =
        useState(false);
    const [receiptPackageReady, setReceiptPackageReady] = useState(false);
    const [receiptSummaryNotifications, setReceiptSummaryNotifications] =
        useState(false);
    const [receiptBatching, setReceiptBatching] = useState(false);
    const [period, setPeriod] = useState<string | undefined>(
        undefined
    );

    const [toggle, setToggle] = useState(false);
    const [, setIsLoading] = useState<boolean>(true);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [error, setError] = useState<string>();

    useEffect(() => {
        const getConfiguration = async () => {
            try {
                const client = ServiceCreator.getNotificationClient();
                const type = 'ReceiptSummaryPeriod';
                const result = await client.getSystemConfiguration(type);
                setPeriod(result?.value ?? 'Monthly');
            } catch { }
        };

        getConfiguration();
    }, []);

    const onAllDocumentNotificationChange = (checked: boolean) => {
        setAllDocumentNotification(checked);
        setDocumentCancelled(checked);
        setDocumentReceived(checked);
        setDocumentRejected(checked);
        setDocumentValidated(checked);
        setDocumentNotification(checked);
    };

    const allChecked = (
        checkbox1: boolean,
        checkbox2: boolean,
        checkbox3: boolean,
        checkbox4: boolean
    ) => {
        return checkbox1 && checkbox2 && checkbox3 && checkbox4;
    };

    const onDocumentReceivedChange = (checked: boolean) => {
        setDocumentReceived(checked);
        setDocumentNotification(checked);
        setAllDocumentNotification(
            allChecked(
                documentCancelled,
                documentRejected,
                documentValidated,
                checked
            )
        );
    };

    const onDocumentCancelledChange = (checked: boolean) => {
        setDocumentCancelled(checked);
        setDocumentNotification(checked);
        setAllDocumentNotification(
            allChecked(
                documentReceived,
                documentRejected,
                documentValidated,
                checked
            )
        );
    };

    const onDocumentRejectedChange = (checked: boolean) => {
        setDocumentRejected(checked);
        setDocumentNotification(checked);
        setAllDocumentNotification(
            allChecked(
                documentReceived,
                documentCancelled,
                documentValidated,
                checked
            )
        );
    };

    const onDocumentValidatedChange = (checked: boolean) => {
        setDocumentValidated(checked);
        setDocumentNotification(checked);
        setAllDocumentNotification(
            allChecked(
                documentReceived,
                documentCancelled,
                documentRejected,
                checked
            )
        );
    };

    const onPackageReadyChange = (checked: boolean) => {
        setPackageReady(checked);
    };
    const onCodesRequestsChange = (checked: boolean) => {
        setCodesRequests(checked);
    };

    const onToggleChange = (checked: boolean) => {
        setToggle(checked);
    };

    const getNotificationChannel = () => {
        switch (notificationChannel) {
            case 1:
                return NotificationChannels.Email;
            case 2:
                return NotificationChannels.Sms;
            case 3:
                return NotificationChannels.System;
            default:
                return undefined;
        }
    };
    const saveForm = async () => {
        setIsSaving(true);
        setIsLoading(true);
        const request = new RecipientPreferenceRequestDto();
        const recipients: RecipientNotificationPreferenceRequestDto[] = [];
        if (packageReady) {
            const recepient = new RecipientNotificationPreferenceRequestDto();
            recepient.isSubscribed = packageReady;
            recepient.notificationTypeId =
                NotificationTypes.ReceiveDownloadReady;
            recipients.push(recepient);
        }
        if (receiptPackageReady) {
            recipients.push(
                new RecipientNotificationPreferenceRequestDto({
                    isSubscribed: receiptPackageReady,
                    notificationTypeId:
                        NotificationTypes.ReceiptPackageDownloadReady,
                })
            );
        }
        if (documentReceived) {
            const recepient = new RecipientNotificationPreferenceRequestDto();
            recepient.isSubscribed = true;
            recepient.isBatched = toggle;
            recepient.notificationTypeId = NotificationTypes.DocumentReceived;
            recipients.push(recepient);
        }
        if (documentValidated) {
            const recepient = new RecipientNotificationPreferenceRequestDto();
            recepient.isSubscribed = true;
            recepient.isBatched = toggle;
            recepient.notificationTypeId = NotificationTypes.DocumentValidated;
            recipients.push(recepient);
        }
        if (documentCancelled) {
            const recepient = new RecipientNotificationPreferenceRequestDto();
            recepient.isSubscribed = true;
            recepient.isBatched = toggle;
            recepient.notificationTypeId = NotificationTypes.DocumentCancelled;
            recipients.push(recepient);
            recipients.push(
                new RecipientNotificationPreferenceRequestDto({
                    ...recepient,
                    notificationTypeId:
                        NotificationTypes.DocumentCancellationInitiated,
                })
            );
            recipients.push(
                new RecipientNotificationPreferenceRequestDto({
                    ...recepient,
                    notificationTypeId:
                        NotificationTypes.DocumentCancellationDeclined,
                })
            );
        }
        if (documentRejected) {
            const recepient = new RecipientNotificationPreferenceRequestDto();
            recepient.isSubscribed = true;
            recepient.isBatched = toggle;
            recepient.notificationTypeId = NotificationTypes.DocumentRejected;
            recipients.push(recepient);
            recipients.push(
                new RecipientNotificationPreferenceRequestDto({
                    ...recepient,
                    notificationTypeId:
                        NotificationTypes.DocumentRejectionDeclined,
                })
            );
            recipients.push(
                new RecipientNotificationPreferenceRequestDto({
                    ...recepient,
                    notificationTypeId:
                        NotificationTypes.DocumentRejectionInitiated,
                })
            );
        }

        if (CodesRequests) {
            const recepient = new RecipientNotificationPreferenceRequestDto();
            recepient.isSubscribed = true;
            recepient.isBatched = toggle;
            recepient.notificationTypeId = NotificationTypes.CodeUsageApproved;
            recipients.push(recepient);
            recipients.push(
                new RecipientNotificationPreferenceRequestDto({
                    ...recepient,
                    notificationTypeId: NotificationTypes.CodeUsageRejected,
                })
            );
        }
        if (receiptNotifications && receiptProcessingComplete) {
            const recepient = new RecipientNotificationPreferenceRequestDto();
            recepient.isSubscribed = true;
            recepient.isBatched = receiptBatching;
            recepient.notificationTypeId = NotificationTypes.ReceiptValidated;
            recipients.push(recepient);
        }
        if (receiptNotifications && receiptReceivedByTaxpayer) {
            const recepient = new RecipientNotificationPreferenceRequestDto();
            recepient.isSubscribed = true;
            recepient.isBatched = receiptBatching;
            recepient.notificationTypeId = NotificationTypes.ReceiptReceived;
            recipients.push(recepient);
        }
        if (receiptSummaryNotifications) {
            recipients.push(new RecipientNotificationPreferenceRequestDto({
                isSubscribed: true,
                notificationTypeId:
                    NotificationTypes.ReceiptSummary,
            }));
        }

        request.batchInterval = batchInterval === 0 ? undefined : batchInterval;
        request.notificationChannelId = getNotificationChannel();
        request.recipientNotificationPreferences = recipients;
        const response = await updateNotifications(request);

        if (isNull((await response.payload).lastError)) {
            onSave();
        }

        setIsLoading(false);
        setIsSaving(false);
    };

    useEffect(() => {
        if (notifications) {
            setNotificationChannel(notifications?.notificationChannel?.id);
            setBatchInterval(
                notifications.batchInterval === null
                    ? 0
                    : notifications.batchInterval
            );
            const pref = notifications.recipientNotificationPreferences;
            let DocumentReceived = true;
            let DocumentCancelled = true;
            let DocumentRejected = true;
            let DocumentValidated = true;
            setDocumentReceived(true);
            setDocumentCancelled(true);
            setDocumentRejected(true);
            setDocumentValidated(true);
            if (pref && pref.length > 0) {
                for (let i = 0; i < pref.length; i += 1) {
                    const notify = pref[i];

                    if (notify !== undefined) {
                        switch (notify.notificationTypeId) {
                            case NotificationTypes.DocumentReceived:
                                setToggle(notify.isBatched || false);
                                setDocumentReceived(
                                    notify.isSubscribed || false
                                );
                                DocumentReceived = notify.isSubscribed || false;
                                setDocumentNotification(true);
                                break;
                            case NotificationTypes.DocumentCancelled:
                                setDocumentCancelled(
                                    notify.isSubscribed || false
                                );
                                DocumentCancelled =
                                    notify.isSubscribed || false;
                                setToggle(notify.isBatched || false);
                                setDocumentNotification(true);
                                break;
                            case NotificationTypes.DocumentRejected:
                                setDocumentRejected(
                                    notify.isSubscribed || false
                                );
                                DocumentRejected = notify.isSubscribed || false;
                                setToggle(notify.isBatched || false);
                                setDocumentNotification(true);
                                break;
                            case NotificationTypes.DocumentValidated:
                                setDocumentValidated(
                                    notify.isSubscribed || false
                                );
                                DocumentValidated =
                                    notify.isSubscribed || false;
                                setToggle(notify.isBatched || false);
                                setDocumentNotification(true);
                                break;
                            case NotificationTypes.ReceiveDownloadReady:
                                setPackageReady(notify.isSubscribed || false);
                                break;
                            case NotificationTypes.ReceiptPackageDownloadReady:
                                setReceiptPackageReady(notify.isSubscribed || false);
                                break;
                            case NotificationTypes.CodeUsageApproved:
                                setCodesRequests(notify.isSubscribed || false);
                                break;
                            case NotificationTypes.ReceiptValidated:
                                setReceiptNotifications(true);
                                setReceiptBatching(notify.isBatched || false);
                                setReceiptProcessingComplete(
                                    notify.isSubscribed || false
                                );
                                break;
                            case NotificationTypes.ReceiptReceived:
                                setReceiptNotifications(true);
                                setReceiptBatching(notify.isBatched || false);
                                setReceiptReceivedByTaxpayer(
                                    notify.isSubscribed || false
                                );
                                break;
                            case NotificationTypes.ReceiptSummary:
                                setReceiptBatching(notify.isBatched || false);
                                setReceiptSummaryNotifications(
                                    notify.isSubscribed || false
                                );
                                break;
                            default:
                                break;
                        }
                    }
                }
                if (
                    DocumentReceived &&
                    DocumentCancelled &&
                    DocumentRejected &&
                    DocumentValidated
                ) {
                    setAllDocumentNotification(true);
                }
            }
        }
    }, [notifications]);

    useEffect(() => {
        if (lastError) {
            setError(lastError?.serviceError?.toString());
        }
    }, [lastError]);

    const NotificationChannel: IDropdownOption[] = [
        { key: 1, text: t('Email') },
        { key: 2, text: t('SMS') },
        { key: 3, text: t('ERP') },
    ];

    const BatchDeliveryPeriod: IDropdownOption[] = [
        { key: 0, text: t('Immediate/On time') },
        { key: 2, text: t('2 Hours') },
        { key: 6, text: t('6 Hours') },
        { key: 12, text: t('12 Hours') },
        { key: 24, text: t('24 Hours') },
        { key: 36, text: t('36 Hours') },
    ];

    useEffect(() => {
        if (!hidden) {
            setError('');
        }
    }, [hidden]);

    return (
        <Dialog
            containerClassName='notificationPreference'
            hidden={hidden}
            onDismiss={() => {
                onClose();
            }}
            maxWidth={450}
            title={(
                <div>
                    <Icon iconName="Settings" />{' '}
                    <span>{t('form.Taxpayer.NotificationPreferences')}</span>
                </div>
            )}
        >
            <div className="contentNotification">
                {error && (
                    <MessageBar
                        messageBarType={MessageBarType.error}
                        isMultiline={false}
                        onDismiss={() => setError('')}
                    >
                        {error}
                    </MessageBar>
                )}
                <div className="choose">
                    {t('form.Notification.ChooseNotification')}
                </div>
                <div className="dropdown">
                    <Dropdown
                        selectedKey={notificationChannel}
                        options={NotificationChannel}
                        label={t('form.Notification.NotificationChannel')}
                        onChange={(_, option) =>
                            setNotificationChannel(Number(option?.key) || 0)
                        }
                    />
                    <Dropdown
                        selectedKey={batchInterval}
                        options={BatchDeliveryPeriod}
                        label={t('form.Notification.BatchDeliveryPeriod')}
                        onChange={(_, option) => {
                            setBatchInterval(Number(option?.key) || 0);
                            if (option?.key === 0) {
                                onToggleChange(false);
                                setReceiptBatching(false);
                            } else {
                                onToggleChange(true);
                                setReceiptBatching(true);
                            }
                        }}
                    />
                </div>
                <div className='headers'>
                    <p>
                        {t('form.Notification.Subscriptions')}
                    </p>
                    <p>
                        {t('form.Notification.Delivery')}
                    </p>
                </div>
                <hr />
                <div className='contentBody'>
                    <div className='checkBox-content'>
                        <div className="mainText">
                            <Checkbox
                                label={t('form.Notification.DocumentNotification')}
                                className="header"
                                checked={allDocumentNotification}
                                onChange={(s, checked) =>
                                    onAllDocumentNotificationChange(checked ?? false)
                                }
                                disabled
                            />
                        </div>
                        <div className="child">
                            <Checkbox
                                label={t('form.Notification.DocumentReceived')}
                                checked={documentReceived}
                                onChange={(s, checked) =>
                                    onDocumentReceivedChange(checked ?? false)
                                }
                                disabled
                            />
                        </div>
                        <div className="child">
                            <Checkbox
                                label={t('form.Notification.DocumentCancelled')}
                                checked={documentCancelled}
                                onChange={(s, checked) =>
                                    onDocumentCancelledChange(checked ?? false)
                                }
                                disabled
                            />
                        </div>
                        <div className="child">
                            <Checkbox
                                label={t('form.Notification.DocumentRejected')}
                                checked={documentRejected}
                                onChange={(s, checked) =>
                                    onDocumentRejectedChange(checked ?? false)
                                }
                                disabled
                            />
                        </div>
                        <div className="child">
                            <Checkbox
                                label={t('form.Notification.DocumentValidated')}
                                checked={documentValidated}
                                onChange={(s, checked) =>
                                    onDocumentValidatedChange(checked ?? false)
                                }
                                disabled
                            />
                        </div>
                        <div className="mainText">
                            <Checkbox
                                label={t('form.Notification.DocumentPackageReady')}
                                className="header"
                                checked={packageReady}
                                onChange={(s, checked) =>
                                    onPackageReadyChange(checked ?? false)
                                }
                            />
                        </div>
                    </div>
                    <div className='toggle-content'>
                        <Toggle
                            inlineLabel
                            onText={t('Batching')}
                            offText={t('Batching')}
                            checked={toggle}
                            onChange={(s, checked) =>
                                onToggleChange(checked ?? false)
                            }
                            disabled
                        />
                    </div>
                </div>
                <hr className="hr" />
                <div className='contentBody'>
                    <div className='checkBox-content'>
                        <div className="mainText">
                            <Checkbox
                                label={t('ReceiptNotifications')}
                                className="header"
                                checked={receiptNotifications}
                                onChange={(s, checked) => {
                                    setReceiptNotifications(checked ?? false);
                                    setReceiptProcessingComplete(false);
                                    setReceiptReceivedByTaxpayer(false);
                                }}
                            />
                        </div>
                        <div className="child">
                            <Checkbox
                                label={t('ReceiptProcessingComplete')}
                                checked={receiptProcessingComplete}
                                disabled={!receiptNotifications}
                                onChange={(s, checked) =>
                                    setReceiptProcessingComplete(checked ?? false)
                                }
                            />
                        </div>
                        <div className="child">
                            <Checkbox
                                label={t('ReceiptReceivedByTaxpayer')}
                                disabled={!receiptNotifications}
                                checked={receiptReceivedByTaxpayer}
                                onChange={(s, checked) =>
                                    setReceiptReceivedByTaxpayer(checked ?? false)
                                }
                            />
                        </div>
                        <div className="mainText">
                            <Checkbox
                                label={t('ReceiptPackageReady')}
                                className="header"
                                checked={receiptPackageReady}
                                onChange={(s, checked) =>
                                    setReceiptPackageReady(checked ?? false)
                                }
                            />
                        </div>
                        <div className="flex">
                            <div className='mainText'>
                                <Checkbox
                                    label={t('ReceiptSummaryNotifications')}
                                    checked={receiptSummaryNotifications}
                                    className="header"
                                    onChange={(s, checked) =>
                                        setReceiptSummaryNotifications(checked ?? false)
                                    }
                                />
                            </div>
                            <Spacer direction="horizontal" size={5} />
                            <TooltipHost
                                className=''
                                directionalHint={DirectionalHint.rightBottomEdge}
                                tooltipProps={{
                                    onRenderContent: () => (
                                        <div>{`${t('notificationPeriod')} ${t(period ?? '')}`}</div>
                                    ),
                                }}
                            >
                                <Icon iconName="Info" />
                            </TooltipHost>
                        </div>
                    </div>
                    <div className='toggle-content'>
                        <Toggle
                            inlineLabel
                            onText={t('Batching')}
                            offText={t('Batching')}
                            // disabled={batchInterval === 0 || !receiptNotifications}
                            disabled
                            checked={receiptBatching}
                            onChange={(s, checked) =>
                                setReceiptBatching(checked ?? false)
                            }
                        />
                    </div>
                </div>
                <hr className="hr" />
                <div className='contentBody'>
                    <div className='checkBox-content'>
                        <div className="mainText">
                            <Checkbox
                                label={t('form.Notification.CodesRequests')}
                                checked={CodesRequests}
                                onChange={(s, checked) =>
                                    onCodesRequestsChange(checked ?? false)
                                }
                            />
                        </div>
                    </div>
                </div>

                <div className='footerDialog'>
                    <div className="btns">
                        <PrimaryButton
                            className="cancelBtn"
                            iconProps={{ iconName: 'Cancel' }}
                            text={t('common:cancel')}
                            onClick={onClose}
                        />
                        <PrimaryButton className='saveBtn' iconProps={{ iconName: 'Save' }} onClick={saveForm} text={t('common:save')}>
                            {isSaving && <Spinner size={SpinnerSize.small} />}
                        </PrimaryButton>
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

export default connector(NotificationPreferences);
